import surfaceGLSL from "./utils/surface.glsl";

import normalsGLSL from "./utils/normals.glsl";

const fragmentShader = /* glsl */ `
precision highp float;

varying vec3 vNormal;
varying vec2 vUv;
varying vec3 vWorldPosition;


${surfaceGLSL}
${normalsGLSL}


/*
 * ============================================================
 * LIGHTING
 * ============================================================
 */

uniform vec3 uLightDirection;
uniform vec3 uLightColor;

uniform float uLightIntensity;
uniform float uAmbientIntensity;


/*
 * ============================================================
 * PBR MATERIAL
 * ============================================================
 */

uniform float uMetallic;
uniform float uRoughness;

uniform sampler2D uMetallicMap;
uniform bool uHasMetallicMap;

uniform sampler2D uRoughnessMap;
uniform bool uHasRoughnessMap;


/*
 * ============================================================
 * CONSTANTS
 * ============================================================
 */

const float PI =
    3.14159265359;


/*
 * ============================================================
 * MATERIAL PARAMETERS
 * ============================================================
 */

float getMetallic(
    vec2 uv
) {

    float metallic =
        uMetallic;


    if (
        uHasMetallicMap
    ) {

        metallic *=
            texture2D(
                uMetallicMap,
                uv
            ).r;
    }


    return clamp(
        metallic,
        0.0,
        1.0
    );
}


float getRoughness(
    vec2 uv
) {

    float roughness =
        uRoughness;


    if (
        uHasRoughnessMap
    ) {

        roughness *=
            texture2D(
                uRoughnessMap,
                uv
            ).r;
    }


    /*
     * Avoid pathological values near
     * zero roughness.
     */
    return clamp(
        roughness,
        0.045,
        1.0
    );
}


/*
 * ============================================================
 * GGX NORMAL DISTRIBUTION
 * ============================================================
 */

float distributionGGX(
    vec3 normal,
    vec3 halfway,
    float roughness
) {

    float alpha =
        roughness *
        roughness;


    float alphaSquared =
        alpha *
        alpha;


    float nDotH =
        max(
            dot(
                normal,
                halfway
            ),
            0.0
        );


    float nDotHSquared =
        nDotH *
        nDotH;


    float denominator =
        nDotHSquared *
        (
            alphaSquared -
            1.0
        ) +
        1.0;


    denominator =
        PI *
        denominator *
        denominator;


    return (
        alphaSquared /
        max(
            denominator,
            0.000001
        )
    );
}


/*
 * ============================================================
 * SMITH GEOMETRY
 * ============================================================
 */

float geometrySchlickGGX(
    float nDotDirection,
    float roughness
) {

    /*
     * Common direct-light approximation.
     */
    float r =
        roughness +
        1.0;


    float k =
        (
            r *
            r
        ) /
        8.0;


    return (
        nDotDirection /
        (
            nDotDirection *
            (
                1.0 -
                k
            ) +
            k
        )
    );
}


float geometrySmith(
    vec3 normal,
    vec3 viewDirection,
    vec3 lightDirection,
    float roughness
) {

    float nDotV =
        max(
            dot(
                normal,
                viewDirection
            ),
            0.0
        );


    float nDotL =
        max(
            dot(
                normal,
                lightDirection
            ),
            0.0
        );


    float geometryView =
        geometrySchlickGGX(
            nDotV,
            roughness
        );


    float geometryLight =
        geometrySchlickGGX(
            nDotL,
            roughness
        );


    return (
        geometryView *
        geometryLight
    );
}


/*
 * ============================================================
 * SCHLICK FRESNEL
 * ============================================================
 */

vec3 fresnelSchlick(
    float cosTheta,
    vec3 f0
) {

    return (
        f0 +
        (
            1.0 -
            f0
        ) *
        pow(
            clamp(
                1.0 -
                cosTheta,
                0.0,
                1.0
            ),
            5.0
        )
    );
}


/*
 * ============================================================
 * MAIN
 * ============================================================
 */

void main() {

    vec2 surfaceUv =
        transformSurfaceUv(
            vUv
        );


    vec3 albedo =
        getSurfaceColor(
            surfaceUv
        );


    vec3 normal =
        getSurfaceNormal(
            surfaceUv
        );


    float ao =
        getAmbientOcclusion(
            surfaceUv
        );


    vec3 emissive =
        getEmissiveColor(
            surfaceUv
        );


    float metallic =
        getMetallic(
            surfaceUv
        );


    float roughness =
        getRoughness(
            surfaceUv
        );


    /*
     * World-space directions.
     */
    vec3 viewDirection =
        normalize(
            cameraPosition -
            vWorldPosition
        );


    vec3 lightDirection =
        normalize(
            uLightDirection
        );


    vec3 halfway =
        normalize(
            viewDirection +
            lightDirection
        );


    float nDotL =
        max(
            dot(
                normal,
                lightDirection
            ),
            0.0
        );


    float nDotV =
        max(
            dot(
                normal,
                viewDirection
            ),
            0.0
        );


    /*
     * Dielectric surfaces use an
     * approximately 4% F0.
     *
     * Metals derive F0 from albedo.
     */
    vec3 f0 =
        vec3(
            0.04
        );


    f0 =
        mix(
            f0,
            albedo,
            metallic
        );


    /*
     * Microfacet terms.
     */
    float distribution =
        distributionGGX(
            normal,
            halfway,
            roughness
        );


    float geometry =
        geometrySmith(
            normal,
            viewDirection,
            lightDirection,
            roughness
        );


    vec3 fresnel =
        fresnelSchlick(
            max(
                dot(
                    halfway,
                    viewDirection
                ),
                0.0
            ),
            f0
        );


    /*
     * Cook-Torrance specular BRDF.
     */
    vec3 numerator =
        distribution *
        geometry *
        fresnel;


    float denominator =
        4.0 *
        nDotV *
        nDotL;


    vec3 specular =
        numerator /
        max(
            denominator,
            0.0001
        );


    /*
     * Energy conservation.
     *
     * Fresnel-reflected energy cannot
     * also contribute to diffuse.
     */
    vec3 kS =
        fresnel;


    vec3 kD =
        vec3(
            1.0
        ) -
        kS;


    /*
     * Metals have effectively no
     * diffuse component.
     */
    kD *=
        1.0 -
        metallic;


    vec3 diffuse =
        kD *
        albedo /
        PI;


    /*
     * Directional-light radiance.
     */
    vec3 radiance =
        uLightColor *
        uLightIntensity;


    vec3 directLighting =
        (
            diffuse +
            specular
        ) *
        radiance *
        nDotL;


    /*
     * Temporary ambient term.
     *
     * We'll replace this with IBL
     * in the next stage.
     */
    vec3 ambient =
        albedo *
        uAmbientIntensity *
        ao;


    vec3 finalColor =
        directLighting +
        ambient +
        emissive;


    /*
     * Simple Reinhard tone mapping.
     */
    finalColor =
        finalColor /
        (
            finalColor +
            vec3(
                1.0
            )
        );


    gl_FragColor =
        vec4(
            finalColor,
            1.0
        );
}

`;

export default fragmentShader;
